import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../styles/panel/user/ChatArchive.css";
import { getChatMyStores } from "../../../api/chat";
import moment from "jalali-moment";
import useChatOnlineListSocket from "../../../hooks/useChatOnlineListSocket";

// حرف اول اسم فروشگاه برای آواتار
const initial = (name) => (name ? name.trim().charAt(0) : "?");

// خلاصه‌ی آخرین پیام: حداکثر ۵ کلمه، و اگه متنی نبود ولی فایل بود بنویسه «فایل»
// توجه: بسته به اسم فیلدهای واقعی توی API (text/content/message و file/attachment/file_url)
// ممکنه لازم باشه اسم فیلدها رو با بک‌اند هماهنگ کنی.
const lastMessagePreview = (lastMessage) => {
  console.log(lastMessage);
  const text =
    lastMessage?.body ?? lastMessage?.content ?? lastMessage?.message;
  const hasFile = Boolean(
    lastMessage?.file ||
    lastMessage?.attachment ||
    lastMessage?.file_url ||
    lastMessage?.body == "",
  );

  if (!text || !text.trim()) {
    return hasFile ? "فایل" : "—";
  }

  const words = text.trim().split(/\s+/);
  return words.length > 5 ? `${words.slice(0, 5).join(" ")}…` : words.join(" ");
};

export default function ChatArchive() {
  const [query, setQuery] = useState("");
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // اندازه‌ی صفحه رو از اولین پاسخ واقعی بک‌اند حدس می‌زنیم (چون بک‌اند
  // count/next/previous/results برمی‌گردونه، نه شماره‌ی کل صفحات). این فقط
  // برای نمایش شماره صفحات استفاده می‌شه؛ فعال/غیرفعال بودن دکمه‌های
  // قبلی/بعدی همیشه از next/previous واقعی گرفته می‌شه.
  const pageSizeRef = useRef(null);

  // با تغییر جستجو، صفحه برمی‌گرده به ۱ و یک بار دیگه با اون همین افکت
  // درخواست می‌فرسته (پس اینجا لود نمی‌کنیم تا دوبار فچ نشه). اولین بار که
  // این افکت اجرا می‌شه (mount) رو نادیده می‌گیریم چون useEffect صفحه خودش
  // یک بار لود اولیه رو انجام می‌ده.
  const isFirstQueryRun = useRef(true);
  useEffect(() => {
    if (isFirstQueryRun.current) {
      isFirstQueryRun.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        loadChats(query, 1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    loadChats(query, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const queryRef = useRef(query);
  const pageRef = useRef(page);
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    return () => clearTimeout(refreshTimerRef.current);
  }, []);

  const loadChats = async (search = "", pageNumber = 1, showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await getChatMyStores({
        search,
        page: pageNumber,
        page_size: 7,
      });
      // ساختار واقعی پاسخ بک‌اند (استاندارد Django REST Framework):
      // { count, next, previous, results }
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setChats(list);
      const count = payload?.count ?? list.length;
      setTotal(count);
      setHasNext(Boolean(payload?.next));
      setHasPrevious(Boolean(payload?.previous));

      if (Array.isArray(payload)) {
        setLastPage(1);
      } else {
        // اندازه‌ی صفحه رو فقط یک بار، از اولین پاسخ حدس می‌زنیم
        if (pageSizeRef.current == null && list.length > 0) {
          pageSizeRef.current = list.length;
        }
        const size = pageSizeRef.current || list.length || 1;
        setLastPage(Math.max(1, Math.ceil(count / size)));
      }
      setPage(pageNumber);
    } catch (err) {
      console.log(err);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // رفرش بی‌صدا (بدون لودر) با جستجو و صفحه‌ی فعلی. اگه چند چت پشت‌سرهم
  // آپدیت بشن، فقط یک درخواست فرستاده می‌شه.
  const handleChatUpdate = () => {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      loadChats(queryRef.current, pageRef.current, false);
    }, 300);
  };

  const goToPage = (p) => {
    if (p < 1 || p === page || loading) return;
    if (p > page && !hasNext && p > lastPage) return;
    setPage(p);
  };

  // شماره صفحاتی که دکمه‌شون نمایش داده می‌شه (حداکثر ۵ تا، دور صفحه‌ی جاری)
  const pageNumbers = () => {
    const span = 2;
    const start = Math.max(1, page - span);
    const end = Math.min(lastPage, page + span);
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };

  const openChat = (chatId) => {
    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, unread_count: 0 } : c)),
    );
    // مسیر داینامیک بر اساس پنل جاری (ادمین، کاربر یا پشتیبان)
    const basePath = location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
    navigate(`${basePath}/${chatId}`);
  };

  // یک سوکت سراسری که مستقل از اینکه چه چت‌هایی توی لیست هستن، از لحظه‌ی
  // mount وصله. برای همین چت‌های کاملاً جدید (که هنوز توی لیست نیستن و هیچ‌کس
  // به روم مخصوص خودشون وصل نشده) هم پوشش داده می‌شن.
  useChatOnlineListSocket({ onUpdate: handleChatUpdate });

  return (
    <div className="chat-archive">
      <div className="chat-archive-head">
        <div>
          <h1>آرشیو چت‌ها</h1>
          <p>گفتگوهای پیشین شما </p>
        </div>
        <div className="chat-search">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="جستجوی فروشگاه یا مشتری..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="chat-table">
        <div className="chat-table-row chat-table-header">
          <span>مشتری</span>
          <span>فروشگاه</span>
          <span>آخرین پیام</span>
          <span>آخرین ساعت چت</span>
        </div>

        {loading && <div className="chat-empty">در حال بارگذاری...</div>}

        {!loading &&
          chats.map((chat) => (
            <div
              className={`chat-table-row${chat.unread_count > 0 ? " has-unread" : ""}`}
              key={chat.id}
              role="button"
              tabIndex={0}
              onClick={() => openChat(chat.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openChat(chat.id);
                }
              }}
            >
              <span className="chat-cell chat-shop">
                <span className="chat-avatar-wrap">
                  <span className="chat-avatar">
                    {initial(chat.customer_name)}
                  </span>
                  {chat.unread_count > 0 && (
                    <span className="unread-badge">
                      {chat.unread_count > 99 ? "۹۹+" : chat.unread_count}
                    </span>
                  )}
                </span>
                <span className="chat-shop-name">{chat.customer_name}</span>
              </span>
              <span className="chat-cell">{chat.store_name}</span>
              <span className="chat-cell chat-message">
                {lastMessagePreview(chat.last_message)}
              </span>
              <span className="chat-cell chat-time">
                {moment(chat.last_message?.created_at).format(
                  "HH:mm - jYYYY/jMM/jDD",
                ) || "—"}
              </span>
            </div>
          ))}

        {!loading && chats.length === 0 && (
          <div className="chat-empty">چتی با این مشخصات پیدا نشد</div>
        )}

        {!loading && (hasNext || hasPrevious || lastPage > 1) && (
          <div className="chat-pagination">
            <span className="chat-pagination-total">
              {total.toLocaleString("fa-IR")} چت — صفحه{" "}
              {page.toLocaleString("fa-IR")} از{" "}
              {lastPage.toLocaleString("fa-IR")}
            </span>
            <div className="chat-pagination-controls">
              <button
                type="button"
                className="chat-page-btn chat-page-nav"
                disabled={!hasPrevious}
                onClick={() => goToPage(page - 1)}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>

              {pageNumbers()[0] > 1 && (
                <>
                  <button
                    type="button"
                    className="chat-page-btn"
                    onClick={() => goToPage(1)}
                  >
                    ۱
                  </button>
                  {pageNumbers()[0] > 2 && (
                    <span className="chat-page-dots">…</span>
                  )}
                </>
              )}

              {pageNumbers().map((p) => (
                <button
                  type="button"
                  key={p}
                  className={`chat-page-btn${p === page ? " active" : ""}`}
                  onClick={() => goToPage(p)}
                >
                  {p.toLocaleString("fa-IR")}
                </button>
              ))}

              {pageNumbers()[pageNumbers().length - 1] < lastPage && (
                <>
                  {pageNumbers()[pageNumbers().length - 1] < lastPage - 1 && (
                    <span className="chat-page-dots">…</span>
                  )}
                  <button
                    type="button"
                    className="chat-page-btn"
                    onClick={() => goToPage(lastPage)}
                  >
                    {lastPage.toLocaleString("fa-IR")}
                  </button>
                </>
              )}

              <button
                type="button"
                className="chat-page-btn chat-page-nav"
                disabled={!hasNext}
                onClick={() => goToPage(page + 1)}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
